import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { trackEvent } from '../../../analytics/sml/analytics';
import { TypedMemo } from '../../types';
import { errorCodeOf } from './errorCodeOf';
import { ErrorViewProps } from './props';
import { SMLButton } from '../SMLButton/SMLButton';
import { useCustomerPortalUrl } from '../../hooks/useCustomerPortalUrl';
import { useMyTeamRole } from '../../../team/useMyTeamRole';
import { useWorkspaceOwner } from '../../../team/useWorkspaceOwner';
import { TeamOwnerNote } from '../../../team/TeamOwnerNote';
import { useIsNativeEmbed } from '../../../music24/navigation/hooks/useIsNativeEmbed';
import { NativeUpgradeNote } from '../../../music24/components/NativeUpgradeNote/NativeUpgradeNote';
import { useOpenPlans } from '../../../music24/trialStatus/useOpenPlans';

const ErrorViewComponent: React.FC<ErrorViewProps> = ({ error, retry }) => {
  const intl = useIntl();
  // Ruling 8: only the workspace owner manages billing — design o3 replaces the member's billing
  // link with `TeamOwnerNote`, never a disabled button.
  const isMember = useMyTeamRole() === 'MEMBER';
  const owner = useWorkspaceOwner();
  // App Store 3.1.1 / Play billing: a native app's web view links to the app's own Subscription
  // pane, never to the Stripe portal.
  const isNativeEmbed = useIsNativeEmbed();

  const isOperationalError = error instanceof FYMAPIError && error.isOperational;
  // A browse-credit or follow-limit error from tRPC (`data.errorType`). Only these: no server
  // sends `errorType: 'FORBIDDEN'`, and a plain permission error must never read as a limit.
  const isCreditError = Boolean(
    error &&
      typeof error === 'object' &&
      'data' in error &&
      error.data &&
      typeof error.data === 'object' &&
      'errorType' in error.data &&
      (error.data.errorType === 'NO_BROWSE_CREDITS' ||
        error.data.errorType === 'BROWSE_CREDITS_EXCEEDED' ||
        error.data.errorType === 'NO_FOLLOW_LIMIT' ||
        error.data.errorType === 'FOLLOW_LIMIT_EXCEEDED')
  );

  const code = errorCodeOf(error);
  useEffect(() => {
    trackEvent('error_shown', { surface: 'errorView', code });
  }, [code]);

  const { portalUrl, isLoading: isLoadingPortalUrl } = useCustomerPortalUrl({ enabled: isCreditError });
  // A Free or trial owner has no Stripe subscription, so the portal url is null: open the plans.
  const openPlans = useOpenPlans();

  if (isCreditError) {
    return (
      <div className="d-flex justify-content-center align-items-center w-100 flex-column p-5 flex-grow-1" role="alert">
        <h6 className="ui-text text-center mb-3">
          {(error as any)?.message ||
            intl.formatMessage({
              id: 'app.errors.credits.message',
              defaultMessage: 'You have reached your limit. Upgrade to access more features.',
            })}
        </h6>
        {isMember ? (
          <TeamOwnerNote owner={owner} className="text-start" />
        ) : isNativeEmbed ? (
          <NativeUpgradeNote className="text-start" />
        ) : portalUrl ? (
          <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
            <SMLButton disabled={isLoadingPortalUrl}>
              {intl.formatMessage({
                id: 'app.errors.credits.upgrade_button',
                defaultMessage: 'Upgrade Now',
              })}
            </SMLButton>
          </a>
        ) : (
          <SMLButton disabled={isLoadingPortalUrl} onClick={openPlans}>
            {intl.formatMessage({
              id: 'app.errors.credits.upgrade_button',
              defaultMessage: 'Upgrade Now',
            })}
          </SMLButton>
        )}
        {Boolean(retry) && (
          <SMLButton outline onClick={retry} className="mt-2">
            {intl.formatMessage({
              id: 'app.errors.fym_api.try_again',
              defaultMessage: 'Try again',
            })}
          </SMLButton>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex justify-content-center align-items-center w-100 flex-column p-5 flex-grow-1" role="alert">
      <h6 className="ui-text text-center mb-3">
        {isOperationalError
          ? error.message
          : intl.formatMessage({
              id: 'app.errors.fym_api.default',
              defaultMessage: `Something went wrong. Please try again, and if the problem persists, contact support@music24.com.`,
            })}
      </h6>
      {retry && (
        <SMLButton onClick={retry}>
          {intl.formatMessage({
            id: 'app.errors.fym_api.try_again',
            defaultMessage: 'Try again',
          })}
        </SMLButton>
      )}
    </div>
  );
};

export const ErrorView = TypedMemo(ErrorViewComponent);
